import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, Inbox, Star, Trophy, Medal, Award, ArrowRight, Clock, Bell, Quote } from "lucide-react";
import { SiFacebook, SiInstagram } from "react-icons/si";
import NewsletterModal from "@/components/newsletter-modal";
import { updatePageSEO } from "@/lib/seo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import type { Book, BlogPost } from "@shared/schema";

const Home = ({ onOpenMailingList }: { onOpenMailingList: () => void }) => {
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);

  useEffect(() => {
    updatePageSEO({
      title: "Diane Corso - Horror Author | Broken Things & Dark Fiction",
      description: "Diane Corso is a San Francisco supernatural horror and thriller author. Discover Broken Things and explore spine-chilling tales that blur the line between nightmare and reality.",
      keywords: "Diane Corso, Diane Corso Author, Broken Things, horror author, supernatural horror, thriller writer, San Francisco author, dark fiction",
      ogImage: "https://www.dianecorsofiction.com/attached_assets/broken things book rev (1)_1756509124921.webp",
      canonical: "https://www.dianecorsofiction.com/"
    });
  }, []);

  // Helper function to truncate text
  const truncateText = (text: string, maxLength: number = 200): string => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  const { data: featuredBook } = useQuery<Book>({
    queryKey: ["/api/books/featured"],
  });

  const { data: books = [] } = useQuery<Book[]>({
    queryKey: ["/api/books"],
  });

  const { data: blogPosts = [] } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog"],
  });

  const otherBooks = books.filter(book => book.isFeatured !== "true");
  const latestPosts = blogPosts.slice(0, 3);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 z-0">
          <img
            src="/attached_assets/IMG_5562_1760682308478.webp"
            alt="Dark atmospheric scene with ornate wooden furniture and mystical elements"
            className="w-full h-full object-cover opacity-40"
            decoding="async"
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gothic-black/60 via-gothic-black/70 to-gothic-black/90"></div>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-6xl font-cinzel font-bold mb-6 text-gothic-white animate-fade-in">
            Broken Things Relaunch
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-300 animate-fade-in-delay max-w-2xl mx-auto">
            Broken Things relaunches April 14, 2026 through Cantwell Rock Books. Join The Readers Vault and get a free copy of Skin and Bones — Diane's gift to new subscribers.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              asChild
              className="bg-blood-red hover:bg-deep-red text-white px-8 py-4 text-lg font-semibold transition-all duration-300 hover:shadow-2xl hover:shadow-blood-red/25 transform hover:-translate-y-1"
              data-testid="buy-broken-things-hero"
            >
              <a href="https://www.amazon.com/dp/B0GW14WNJ1" target="_blank" rel="noopener noreferrer">
                <BookOpen className="mr-2" size={20} />
                Buy "Broken Things"
              </a>
            </Button>
            <Button
              variant="outline"
              className="border-2 border-gothic-silver hover:border-blood-red text-gothic-white hover:text-blood-red px-8 py-4 text-lg font-semibold transition-all duration-300 transform hover:-translate-y-1 bg-transparent hover:bg-transparent"
              data-testid="join-mailing-list-hero"
              onClick={onOpenMailingList}
            >
              <Inbox className="mr-2" size={20} />
              Enter The Reader Vault
            </Button>
          </div>
        </div>
      </section>
      {/* About Diane Mini Bio */}
      <section className="py-20 bg-gothic-charcoal">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/images/diane-corso-headshot.webp"
                alt="Diane Corso - Professional author photo"
                className="w-full max-w-md mx-auto rounded-xl shadow-2xl"
                data-testid="author-photo"
                loading="lazy"
                decoding="async"
                width="800"
                height="1000"
                onError={(e) => {
                  console.error('Image failed to load:', e.currentTarget.src);
                }}
              />
            </div>
            <div>
              <h2 className="text-4xl font-playfair font-bold mb-6 text-blood-red">About Diane</h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Diane Corso is a San Francisco–based author of supernatural horror and psychological thrillers that blur the line between nightmare and reality. Her stories explore the quiet terrors of grief, guilt, and the unseen worlds that dwell just beneath our own.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Winner of the 2023 Page Turner Fiction Award and the Best Paranormal/Supernatural Genre Award, Corso's work has been recognized for its haunting prose and emotional depth.
              </p>
              <a
                href="/about"
                className="inline-flex items-center text-blood-red hover:text-gothic-white transition-colors duration-300 font-semibold"
                data-testid="read-full-bio"
              >
                Read Full Bio <ArrowRight className="ml-2" size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* Quiz Lead Magnet Section */}
      <section className="py-20 bg-gothic-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-blood-red uppercase tracking-widest text-sm font-semibold mb-4">Interactive</p>
          <h2 className="text-4xl md:text-5xl font-cinzel font-bold text-gothic-white mb-6">
            What Kind of Thriller Reader Are You?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            5 questions. One chilling revelation. Discover your thriller reader archetype — and unlock a personalized book recommendation from Diane.
          </p>
          <a
            href="/quiz"
            className="inline-flex items-center bg-blood-red hover:bg-red-700 text-white px-10 py-5 text-lg font-bold transition-all duration-300 hover:shadow-2xl hover:shadow-blood-red/25 transform hover:-translate-y-1 uppercase tracking-wider"
          >
            Take the Quiz
            <ArrowRight className="ml-2" size={20} />
          </a>
          <p className="text-sm text-gray-500 mt-6">Join 500+ readers who've discovered their thriller persona</p>
        </div>
      </section>
      {/* Other Books Carousel */}
      <section className="py-20 bg-gothic-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-cinzel font-bold text-blood-red mb-4">Other Works</h2>
            <div className="w-24 h-1 bg-blood-red mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Skin and Bones Book */}
            {otherBooks.filter(book => book.slug === 'skin-and-bones').map((book) => (
              <Card key={book.id} className="bg-gothic-black border-gothic-gray hover:transform hover:scale-105 transition-all duration-300 overflow-hidden shadow-2xl relative">
                <div className="relative">
                  <img
                    src="https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=600&h=300"
                    alt="Coming Soon - Skin and Bones 2nd Edition"
                    className="w-full h-64 object-cover opacity-70"
                    data-testid={`book-cover-${book.slug}`}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gothic-black/60 flex items-center justify-center">
                    <div className="text-center">
                      <Clock className="w-12 h-12 text-blood-red mx-auto mb-2" />
                      <span className="text-2xl font-cinzel font-bold text-gothic-white">COMING SOON</span>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-playfair font-bold mb-3 text-gothic-white">{book.title}</h3>
                  <p className="text-gray-300 mb-4 leading-relaxed">
                    {truncateText(book.synopsis, 150)}
                  </p>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-2">
                      <Bell className="w-4 h-4 text-blood-red" />
                      <span className="text-sm text-gray-400">2024 Release</span>
                    </div>
                    <Button asChild variant="link" className="text-blood-red hover:text-gothic-white font-semibold transition-colors duration-300 p-0 h-auto" data-testid={`learn-more-${book.slug}`} aria-label={`Learn more about ${book.title}`}>
                      <a href="/books">Learn More <ArrowRight className="ml-1 inline" size={16} /></a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Awards Section */}
            <Card className="bg-gothic-black border-gothic-gray p-6 flex flex-col justify-center shadow-2xl">
              <h3 className="text-2xl font-playfair font-bold mb-6 text-blood-red text-center">Awards & Recognition</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Trophy className="text-yellow-400 w-6 h-6" />
                  <div>
                    <p className="font-semibold text-gothic-white">Page Turner Fiction Award</p>
                    <p className="text-sm text-gray-400">2023 Winner</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Medal className="text-yellow-400 w-6 h-6" />
                  <div>
                    <p className="font-semibold text-gothic-white">Page Turner Best Paranormal/Supernatural Genre</p>
                    <p className="text-sm text-gray-400">2023 Winner</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Award className="text-yellow-400 w-6 h-6" />
                  <div>
                    <p className="font-semibold text-gothic-white">PNWA Horror/Paranormal Genre</p>
                    <p className="text-sm text-gray-400">2023 Second Place</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Award className="text-yellow-400 w-6 h-6" />
                  <div>
                    <p className="font-semibold text-gothic-white">CIBA Awards, Paranormal Genre</p>
                    <p className="text-sm text-gray-400">2023 Finalist</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
      {/* Reader Reviews Section */}
      <section className="py-20 bg-gothic-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-cinzel font-bold text-blood-red mb-4">What Readers Are Saying</h2>
            <div className="w-24 h-1 bg-blood-red mx-auto mb-4"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Discover why readers can't put down Diane's spine-chilling tales
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Review 1 */}
            <Card className="bg-gothic-charcoal border-gothic-gray shadow-2xl hover:transform hover:scale-105 transition-all duration-300" data-testid="review-card-1">
              <CardContent className="p-6">
                <Quote className="text-blood-red w-8 h-8 mb-4" />
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-6 italic">
                  "This book goes beyond the horror genre and the typical ghost story. It digs deep into that monster: grief, the daily-ness of loss, and how our identity reshapes itself as we heal."
                </p>
                <div className="border-t border-gothic-gray pt-4">
                  <p className="font-semibold text-gothic-white">Laura Munson</p>
                  <p className="text-sm text-gray-400">New York Times Best-Seller</p>
                </div>
              </CardContent>
            </Card>

            {/* Review 2 */}
            <Card className="bg-gothic-charcoal border-gothic-gray shadow-2xl hover:transform hover:scale-105 transition-all duration-300" data-testid="review-card-2">
              <CardContent className="p-6">
                <Quote className="text-blood-red w-8 h-8 mb-4" />
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-6 italic">
                  "A masterpiece of psychological horror that will keep you awake at night. Corso's ability to blend supernatural elements with deep emotional truths is extraordinary."
                </p>
                <div className="border-t border-gothic-gray pt-4">
                  <p className="font-semibold text-gothic-white">Horror Fiction Review</p>
                  <p className="text-sm text-gray-400">Literary Magazine</p>
                </div>
              </CardContent>
            </Card>

            {/* Review 3 */}
            <Card className="bg-gothic-charcoal border-gothic-gray shadow-2xl hover:transform hover:scale-105 transition-all duration-300" data-testid="review-card-3">
              <CardContent className="p-6">
                <Quote className="text-blood-red w-8 h-8 mb-4" />
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-6 italic">
                  "Broken Things is a haunting tale that lingers long after you turn the final page. Diane Corso has crafted something truly special—horror with heart."
                </p>
                <div className="border-t border-gothic-gray pt-4">
                  <p className="font-semibold text-gothic-white">Sarah M.</p>
                  <p className="text-sm text-gray-400">Amazon Reviewer</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      {/* Social Media Follow Section */}
      <section className="py-20 bg-gothic-charcoal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-cinzel font-bold text-blood-red mb-6">Stay Connected</h2>
          <p className="text-xl text-gray-300 mb-12">Follow Diane's journey across social media</p>

          <div className="flex justify-center space-x-6">
            <a
              href="https://www.facebook.com/thecorsopapers/"
              className="bg-gothic-black hover:bg-blood-red text-gothic-white p-4 rounded-full transition-all duration-300 hover:transform hover:scale-110 hover:shadow-lg"
              aria-label="Follow Diane Corso on Facebook"
              data-testid="social-facebook-main"
              target="_blank"
              rel="noopener noreferrer"
            >
              <SiFacebook size={24} />
            </a>
            <a
              href="https://www.instagram.com/diane_corso_fiction"
              className="bg-gothic-black hover:bg-blood-red text-gothic-white p-4 rounded-full transition-all duration-300 hover:transform hover:scale-110 hover:shadow-lg"
              aria-label="Follow Diane Corso on Instagram"
              data-testid="social-instagram-main"
              target="_blank"
              rel="noopener noreferrer"
            >
              <SiInstagram size={24} />
            </a>
          </div>
        </div>
      </section>
      <NewsletterModal
        isOpen={isNewsletterModalOpen}
        onClose={() => setIsNewsletterModalOpen(false)}
      />
    </div>
  );
};

export default Home;